import React from 'react';
import { PRIVACY_POLICY_PATH } from '@constants/routes';

const TermsAndConditions: React.FC = () => {
  return (
    <div className="container py-5">
    <div className="bg-white p-5 shadow rounded">

      <h1 className="mb-3 font-bold">Terms and Conditions – DRIPSET System</h1>
      <p className="mb-1"><span className="font-bold">Effective Date:</span> 23 May 2025</p>
      <p className="mb-1"><span className="font-bold">Last Updated:</span> 23 May 2025</p>
      <p className="mb-1"><span className="font-bold">Applies to:</span> DRIPSET Android App, iOS App, and Web Portal</p>
      <p className="mb-4"><span className="font-bold">Developer/Manufacturer:</span> Emsensing Technologies Private Limited</p>

      <h5 className="fw-semibold mt-4">1. Acceptance of Terms</h5>
      <p>By downloading, accessing, or using the <strong>DRIPSET</strong> system (mobile or web applications), you agree to comply with and be bound by these Terms and Conditions. If you do not agree, please refrain from using the platform.</p>

      <h5 className="fw-semibold mt-4">2. Purpose of the System</h5>
      <p>DRIPSET is a <strong>Wireless Infusion Monitoring and Control System</strong> for authorized healthcare professionals to:</p>
      <ul className="ps-3" style={{ listStyleType: 'disc' }}>
        <li>Monitoring IV parameters (drip rate, infusion time, quantity).</li>
        <li>Logging drug delivery data.</li>
        <li>Receiving alerts (air bubble, power loss, empty fluid bottle).</li>
        <li>Viewing historical infusion data and analytics.</li>
        <li>Managing patient-linked device sessions.</li>
      </ul>
      <p>It is intended for use <strong>only by trained medical professionals and hospital administrators</strong>.</p>

      <h5 className="fw-semibold mt-4">3. Regulatory Compliance</h5>
      <p><strong>DRIPSET</strong> is developed and maintained under the guidance of <strong>ISO 13485:2016</strong>, with quality procedures ensuring:</p>
      <ul className="ps-3" style={{ listStyleType: 'disc' }}>
        <li>Traceability of updates and audit logs.</li>
        <li>Risk analysis and mitigation as per <strong>ISO 14971</strong>.</li>
        <li>Software life cycle processes aligned with <strong>IEC 62304</strong>.</li>
        <li>Device control and patient safety per applicable <strong>MDR/EU MDR/FDA</strong> guidelines.</li>
      </ul>
      <p>Users must also comply with:</p>
      <ul className="ps-3" style={{ listStyleType: 'disc' }}>
        <li>Indian MDR 2017 or local health authority requirements.</li>
        <li>Internal hospital policies on infusion therapy and device integration.</li>
      </ul>

      <h5 className="fw-semibold mt-4">4. User Responsibilities</h5>
      <p>As a user (nurse/admin), you agree to:</p>
      <ul className="ps-3" style={{ listStyleType: 'disc' }}>
        <li>Provide accurate information during registration.</li>
        <li>Use the system <strong>only for professional and medically approved purposes</strong>.</li>
        <li><strong>Avoid tampering with device settings</strong> outside authorized limits.</li>
        <li>Immediately respond to alerts and notifications to ensure patient safety.</li>
        <li>Maintain <strong>confidentiality of login credentials</strong> and patient-linked data.</li>
        <li>Use the app in compliance with hospital protocols.</li>
      </ul>

      <h5 className="fw-semibold mt-4">5. Data Handling & Privacy</h5>
      <ul className="ps-3" style={{ listStyleType: 'disc' }}>
        <li>All data captured (infusion logs, patient identifiers, alerts) is processed securely and in accordance with our <a href={PRIVACY_POLICY_PATH} className="text-primary text-decoration-none">Privacy Policy</a></li>
        <li>Patient information is pseudonymized; no personally identifiable health data is stored unless authorized by the hospital</li>
        <li>The user is responsible for ensuring that <strong>data entered is accurate</strong> and timely</li>
      </ul>

      <h5 className="fw-semibold mt-4">6. Software Updates and Maintenance</h5>
      <ul className="ps-3" style={{ listStyleType: 'disc' }}>
        <li>The DRIPSET software will receive <strong>regular updates</strong> to maintain safety, performance, and regulatory compliance.</li>
        <li>Users will be notified of critical updates. Failure to install such updates may lead to degraded performance or denial of access.</li>
        <li>Downtime for maintenance may occur periodically and will be communicated in advance wherever possible.</li>
      </ul>

      <h5 className="fw-semibold mt-4">7. Limitations of Use</h5>
      <p>The system:</p>
      <ul className="ps-3" style={{ listStyleType: 'disc' }}>
        <li><strong>Is not intended for unsupervised home use</strong>.</li>
        <li>Must not be used as a standalone decision-making tool for diagnosis or treatment.</li>
        <li>Should not be used with incompatible medical equipment or outside certified environments.</li>
      </ul>

      <h5 className="fw-semibold mt-4">8. Intellectual Property</h5>
      <p>All intellectual property rights in the DRIPSET platform (software, UI, design, documentation) belong to <strong>Emsensing Technologies Pvt. Ltd</strong>.</p>
      <ul className="ps-3" style={{ listStyleType: 'disc' }}>
        <li>Users may not reverse-engineer, copy, or distribute the software without permission.</li>
        <li>Any suggestions or feedback provided may be incorporated without additional obligation.</li>
      </ul>

      <h5 className="fw-semibold mt-4">9. Liability Disclaimer</h5>
      <ul className="ps-3" style={{ listStyleType: 'disc' }}>
        <li>Emsensing Technologies makes every effort to ensure safety and accuracy but <strong>does not assume liability for clinical outcomes</strong>.</li>
        <li>Responsibility for infusion accuracy, drug input, and patient monitoring lies with <strong>authorized healthcare staff</strong>.</li>
        <li>We are not liable for delays or data losses due to power, network, or third-party service failures.</li>
      </ul>

      <h5 className="fw-semibold mt-4">10. Termination of Access</h5>
      <p>We reserve the right to suspend or terminate access to the <strong>DRIPSET</strong> system if:</p>
      <ul className="ps-3" style={{ listStyleType: 'disc' }}>
        <li>Unauthorized use or manipulation is detected.</li>
        <li>Regulatory or safety violations are reported.</li>
        <li>A request is made by the partnering healthcare institution.</li>
      </ul>

      <h5 className="fw-semibold mt-4">11. Jurisdiction and Governing Law</h5>
      <p>These Terms shall be governed in accordance with the laws of <strong>India</strong>, and subject to the exclusive jurisdiction of the courts in <strong>Visakhapatnam, Andhra Pradesh</strong>.</p>

      <h5 className="fw-semibold mt-4">12. Contact Us</h5>
      <div className="mb-4">
        <p className='mb-1'>For support, regulatory queries, or reporting misuse:</p>
        <p className="mb-1"><strong>Emsensing Technologies Private Limited</strong></p>
        <p className="mb-1">AMTZ Campus, Visakhapatnam – 530031, Andhra Pradesh, India</p>
        <p className="mb-1">📧 <a href="mailto:technical@emsense.tech" className="text-decoration-none text-primary">technical@emsense.tech</a></p>
        <p>📞 <a href="tel:+918072011799" className="text-decoration-none text-primary">+91-8072011799</a></p>
      </div>
    </div>
  </div>
  );
};

export default TermsAndConditions;
